import React, { useState, useEffect, useCallback } from "react";
import { Plus } from "lucide-react";
import WaterPlanCreatePopup from "../Water_Creation/WaterPlanCreatePopup";
import { useToast, ToastContainer } from "../../Ui_Components/ToastMessage";
import { getWaterIntakeDetails } from "../../../services/subPlans";
import PermissionGate from "../../Auth/PermissionGate";
import { Button } from "../../Ui_Components/Button";

interface WaterPlanTabProps {
  userId: number;
  nutristId: number;
  isDeactivated?: boolean;
}

interface WaterPlan {
  id: number;
  plan_name: string;
  created_by: string;
  created_on: string;
  effective_from: string;
  quantity: string;
  interval: string;
  status: "Active" | "Scheduled" | "Draft" | "In-Active";
}

const shimmerStyles = `
  @keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
  }
  .shimmer {
    animation: shimmer 2s infinite;
    background: linear-gradient(
      to right,
      #f0f0f0 0%,
      #e0e0e0 20%,
      #f0f0f0 40%,
      #f0f0f0 100%
    );
    background-size: 1000px 100%;
  }
`;

const mapStatus = (shareStatus: string): WaterPlan["status"] => {
  switch (shareStatus) {
    case "active":    return "Active";
    case "scheduled": return "Scheduled";
    case "draft":     return "Draft";
    default:          return "In-Active";
  }
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const WaterPlanTab: React.FC<WaterPlanTabProps> = ({ userId, nutristId, isDeactivated = false }) => {
  const [waterPlans, setWaterPlans] = useState<WaterPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreatePopupOpen, setIsCreatePopupOpen] = useState(false);
  const [nextPlanNumber, setNextPlanNumber] = useState(1);

  const { toasts, removeToast, success, error, warning, info } = useToast();

  const fetchWaterPlans = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getWaterIntakeDetails(userId);

      if (data.success === "1" && Array.isArray(data.water_cycle_data)) {
        const mapped: WaterPlan[] = data.water_cycle_data.map(
          (item: {
            litres_id: number;
            plan_name: string;
            nutritionist_name: string;
            created_on: string;
            effective_from: string;
            value_unit: string;
            interval: string;
            share_status: string;
          }) => ({
            id: item.litres_id,
            plan_name: item.plan_name,
            created_by: item.nutritionist_name,
            created_on: item.created_on,
            effective_from: item.effective_from || "",
            quantity: item.value_unit,
            interval: item.interval || "—",
            status: mapStatus(item.share_status),
          })
        );

        setWaterPlans(mapped);

        const maxNum = mapped.reduce((max, plan) => {
          const match = plan.plan_name.match(/W(\d+)$/i);
          if (match) {
            const num = parseInt(match[1], 10);
            return num > max ? num : max;
          }
          return max;
        }, 0);
        setNextPlanNumber(maxNum + 1);
      } else {
        error("Failed to load", data.message || "Could not fetch water plans.");
      }
    } catch (err) {
      console.error("Error fetching water plans:", err);
      error("Network error", "Failed to fetch water plans. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchWaterPlans();
  }, [fetchWaterPlans]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Active":    return "bg-[#EBF7F4] text-[#0DB5B5]";
      case "Scheduled": return "bg-[#FFF3DE] text-[#FF9500]";
      case "Draft":     return "bg-[#E9E9E9] text-[#202020]";
      case "In-Active": return "bg-[#FFDADA] text-[#FF3B30]";
      default:          return "bg-gray-100 text-gray-600";
    }
  };

  const generatePlanName = () =>
    `TRV/W${String(nextPlanNumber).padStart(2, "0")}`;

  const handlePlanCreated = () => {
    fetchWaterPlans();
  };

  return (
    <>
      <style>{shimmerStyles}</style>
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <div className="h-full flex flex-col bg-white rounded-md border border-[#F3F4F6] overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold font-inter text-[#1F1F1F]">
              Water plan
            </h2>
            <PermissionGate resource="water_plan" action="create">
              <Button
                variant="primary"
                onClick={() => !isDeactivated && setIsCreatePopupOpen(true)}
                disabled={isDeactivated}
                startIcon={<Plus className="w-4 h-4" />}
              >
                Create
              </Button>
            </PermissionGate>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Fixed Table Header */}
          <div className="flex-shrink-0 overflow-x-auto bg-white border-b border-[#111C2D33] custom-scrollbar-overlay">
            <table className="w-full min-w-[1050px] table-fixed">
              <thead>
                <tr className="bg-white">
                  {[
                    ["Sl",             "w-[4%]"],
                    ["Plan name",      "w-[22%]"],
                    ["Quantity",       "w-[13%]"],
                    ["Created By",     "w-[17%]"],
                    ["Effective from", "w-[18%]"],
                    ["Created On",     "w-[17%]"],
                  ].map(([col, width]) => (
                    <th
                      key={col}
                      className={`text-left pt-2 pb-3 px-6 text-[#646464] text-sm 3xl:text-base font-medium font-inter tracking-wider ${width}`}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          </div>

          {/* Scrollable Body */}
          <div className="flex-1 overflow-x-auto overflow-y-auto custom-scrollbar-overlay">
            {loading ? (
              <div className="h-full shimmer" />
            ) : (
              <>
                <table className="w-full min-w-[1050px] table-fixed">
                  <tbody className="bg-white divide-y divide-[#F3F4F6]">
                    {waterPlans.map((plan, index) => (
                      <tr
                        key={plan.id}
                        className="hover:bg-[#F9FEFE] transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[4%]">
                          {String(index + 1).padStart(2, "0")}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap w-[22%]">
                          <div className="flex items-center gap-2">
                            <span className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
                              {plan.plan_name}
                            </span>
                            <span
                              className={`px-1.5 py-0.5 rounded-[24px] text-[8px] 3xl:text-[10px] font-semibold font-inter flex-shrink-0 ${getStatusColor(plan.status)}`}
                            >
                              {plan.status}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[13%]">
                          {plan.quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[17%]">
                          {plan.created_by}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[18%]">
                          {formatDate(plan.effective_from)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] w-[17%]">
                          {plan.created_on}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {waterPlans.length === 0 && (
                  <div className="px-6 py-12 text-center">
                    <p className="text-sm text-gray-500">No water plans found</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {!isDeactivated && (
        <WaterPlanCreatePopup
          isOpen={isCreatePopupOpen}
          onClose={() => setIsCreatePopupOpen(false)}
          onSubmit={handlePlanCreated}
          planName={generatePlanName()}
          userId={userId}
          onShowToast={(type, title, message, duration) => {
            ({ success, error, warning, info })[type]?.(title, message, duration);
          }}
        />
      )}
    </>
  );
};

export default WaterPlanTab;